import { useEffect, useMemo, useRef } from 'react'
import {
  classSlot,
  formatGap,
  formatLapTime,
  gapTrend,
  isCaptureLive,
  neighboursOf,
  recordGap,
  trendVerdict,
  type GapSample,
  type Neighbour,
} from '@racedash/shared'
import { Tile } from '../Tile'
import type { WidgetProps } from './props'
import './gaps.css'

/**
 * The two cars either side, and which way the gaps are moving.
 *
 * The gap on its own is half an answer. Four seconds behind is a place you are
 * about to lose or one you are comfortably holding, and nothing on the tile
 * distinguishes them — so the number that actually decides anything is the
 * direction, and it takes long enough to work out by watching that people stop
 * watching.
 *
 * Ahead is on top and behind underneath, laid out the way a timing screen is,
 * so the tile can be read by position before it is read by label.
 *
 * On the colour: track state is meant to be the only coloured thing on a
 * dashboard, and that rule is worth keeping — a screen where everything is
 * coloured has no signal left. So the yellow and green here are a rail down the
 * side of each row saying which row is which, and not a tone on the tile. The
 * tile is still neutral; the rows are still labelled in words.
 */
export default function Gaps({ car, data, now }: WidgetProps<'gaps'>) {
  const { ahead, behind, position } = neighboursOf(data.standings, car)

  /*
   * The gap's own history, kept here because nothing stores it.
   *
   * The interpreter records each lap's interval, which would give a persistent
   * trend for the car in front and nothing at all for the car behind, whose
   * laps this dashboard does not load. Sampling the live gap is symmetric,
   * costs no extra listener, and measures the thing being asked about rather
   * than a proxy for it. What it cannot do is survive a reload, which is why
   * the tile says how long it has been watching rather than implying it knows
   * the whole race.
   */
  const traces = useRef<Record<string, { key: string; samples: GapSample[] }>>({})

  const sampledAt = data.standings?.updatedAt?.toDate().getTime() ?? null
  const live = isCaptureLive(data.session?.lastSeenAt?.toDate(), now)

  /*
   * The four facts the trace depends on, as primitives.
   *
   * neighboursOf builds a fresh pair of objects every render, so an effect that
   * depended on them would re-run on every tick of the clock for no change at
   * all. Depending on the four values instead is the honest statement of what
   * the trace actually keys on — and it lets the hooks rule check it, rather
   * than being told to look away.
   */
  const aheadCar = ahead?.car ?? null
  const aheadGap = ahead?.gapMs ?? null
  const behindCar = behind?.car ?? null
  const behindGap = behind?.gapMs ?? null
  const sides = useMemo(
    () => [
      { side: 'ahead' as const, car: aheadCar, gapMs: aheadGap },
      { side: 'behind' as const, car: behindCar, gapMs: behindGap },
    ],
    [aheadCar, aheadGap, behindCar, behindGap],
  )

  useEffect(() => {
    if (sampledAt == null || !live) return
    for (const { side, car: key, gapMs } of sides) {
      // No gap in time means no trend: the vendor reports laps instead once a
      // lap separates two cars, and there is nothing to take a difference of.
      if (key == null || gapMs == null) {
        delete traces.current[side]
        continue
      }
      const held = traces.current[side]
      const sample = { at: sampledAt, ms: gapMs }
      traces.current[side] =
        held?.key === key
          ? { key, samples: recordGap(held.samples, sample) }
          : { key, samples: [sample] }
    }
  }, [sampledAt, live, sides])

  /*
   * Only where the history belongs to the car currently in that place.
   *
   * The trace is updated in an effect, which runs after the render that first
   * shows a new neighbour — so for one frame the held history is the previous
   * car's. A frame is a second on a dashboard, and a second of "catching 5.0s"
   * about the wrong car is exactly the kind of thing somebody acts on.
   */
  const trendFor = (side: 'ahead' | 'behind', who: Neighbour | null) => {
    const held = traces.current[side]
    if (!live || !who || held?.key !== who.car) return null
    return gapTrend(held.samples)
  }

  const label = position ? `Gaps · P${position}` : 'Gaps'

  return (
    <Tile label={label}>
      <div className="gaps">
        <Row side="ahead" who={ahead} trend={trendFor('ahead', ahead)} empty="leading" />
        <Row side="behind" who={behind} trend={trendFor('behind', behind)} empty="last" />
      </div>
    </Tile>
  )
}

function Row({
  side,
  who,
  trend,
  empty,
}: {
  side: 'ahead' | 'behind'
  who: Neighbour | null
  trend: ReturnType<typeof gapTrend>
  empty: string
}) {
  const verdict = trendVerdict(trend, side)

  return (
    <div className="gaps-row" data-side={side}>
      <div className="gaps-head">
        <span className="gaps-side">{side === 'ahead' ? 'Ahead' : 'Behind'}</span>
        {who ? (
          <span className="gaps-car" data-slot={classSlot(who.class)}>
            {who.car}
          </span>
        ) : (
          <span className="gaps-none">{empty}</span>
        )}
        <span className="gaps-gap">{who ? formatGap(who.gapMs, who.gapLaps) : ''}</span>
      </div>
      <div className="gaps-trend">
        {/* Said rather than left blank. A car in its box is why the gap is
            moving, and a tile reporting "dropping back 4.0s" without it would
            be describing a pit stop as pace. */}
        {who?.inPits
          ? 'in the pits'
          : verdict && trend
            ? `${verdict} ${formatLapTime(Math.abs(trend.deltaMs))} in ${Math.round(trend.overMs / 1000)}s`
            : ''}
      </div>
    </div>
  )
}
